/**
 * HmSales — kartoteka sprzedaży czytana NA ŻYWO z Symfonii Handel.
 *
 * Cały rozkład ekranu (belka akcji, lewy panel filtrów, stronicowanie, stany „nie mam skąd
 * czytać") mieszka w `HmListShell` — wspólnym dla pięciu kartotek. Tu zostają wyłącznie kolumny
 * i to, co dzieje się po kliknięciu wiersza.
 */

import { useState } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmSaleListItem } from '@saas/shared';
import { Receipt } from 'lucide-react';

import type { ColumnDefinition } from '@/components/configurable-table';
import { HmBulkApprovalButton } from '@/components/handel-magazyn/HmBulkApprovalButton';
import { day, HmListShell } from '@/components/handel-magazyn/HmListShell';
import { InvoiceKindBadge, KsefStatus } from '@/components/handel-magazyn/ksef-cell';
import { ReportsMenuButton } from '@/components/handel-magazyn/ReportsMenuButton';
import {
  defaultReportParams,
  SalesReportParamsModal,
} from '@/components/handel-magazyn/SalesReportParamsModal';
import { useApprovalColumn } from '@/components/handel-magazyn/useApprovalColumn';
import {
  reportDefaults,
  SALES_REPORT_SLUGS,
  type SalesReportParams,
  salesReportPath,
  type SalesReportSlug,
  salesReportTitleKey,
  toSearchParams,
} from '@/lib/handel-magazyn/reports-api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

export function HmSales() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scope = useActiveCompanyScope();
  const [report, setReport] = useState<{
    slug: SalesReportSlug;
    dateFrom: string;
    dateTo: string;
    ids: number[];
  } | null>(null);

  const approvalColumn = useApprovalColumn<HmSaleListItem>('sale', scope.companyId);

  const columns = useMemo<ColumnDefinition<HmSaleListItem>[]>(
    () => [
      {
        id: 'documentNumber',
        header: t('tenant.handelMagazyn.columns.documentNumber'),
        accessor: (row) => row.documentNumber,
        defaultWidth: 170,
        renderCell: (_v: unknown, row: HmSaleListItem) => (
          <span className="flex items-center gap-2">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${row.inBuffer ? 'bg-text-tertiary' : 'bg-emerald-500'}`}
            />
            <span className="font-mono text-xs text-text-primary">{row.documentNumber}</span>
            {/*
              „F" wyłącznie przy JAWNYM `true`. Stan `null` znaczy „nie pobrano" (Symfonia nie
              daje fiskalizacji na liście, tylko w szczegółach) i celowo nie rysuje niczego —
              udawanie wtedy „niezafiskalizowany" podpowiadałoby drugi paragon do dokumentu,
              który swój już ma, a tego nie da się cofnąć.
            */}
            {row.fiscalized === true && (
              <span
                className="rounded bg-sky-100 px-1 text-[11px] font-semibold leading-4 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300"
                title={t('tenant.handelMagazyn.fiscalPrint.listBadgeTitle')}
                data-testid="hm-sale-fiscalized-badge"
              >
                {t('tenant.handelMagazyn.fiscalPrint.listBadge')}
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('tenant.handelMagazyn.columns.status'),
        accessor: (row) =>
          row.inBuffer
            ? t('tenant.handelMagazyn.status.buffer')
            : t('tenant.handelMagazyn.status.issued'),
        defaultWidth: 110,
        renderCell: (_v: unknown, row: HmSaleListItem) => (
          <span
            className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
              row.inBuffer
                ? 'bg-bg-tertiary text-text-secondary'
                : 'bg-emerald-100 text-emerald-800'
            }`}
          >
            {row.inBuffer
              ? t('tenant.handelMagazyn.status.buffer')
              : t('tenant.handelMagazyn.status.issued')}
          </span>
        ),
      },
      {
        id: 'issueDate',
        header: t('tenant.handelMagazyn.columns.issueDate'),
        accessor: (row) => day(row.issueDate),
        defaultWidth: 130,
      },
      {
        id: 'grossValue',
        header: t('tenant.handelMagazyn.columns.gross'),
        accessor: (row) => row.grossValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 130,
        renderCell: (_v: unknown, row: HmSaleListItem) => (
          <span className="font-medium text-text-primary">{row.grossValue}</span>
        ),
      },
      {
        id: 'netValue',
        header: t('tenant.handelMagazyn.columns.net'),
        accessor: (row) => row.netValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 120,
      },
      {
        id: 'vatValue',
        header: t('tenant.handelMagazyn.columns.vat'),
        accessor: (row) => row.vatValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 110,
      },
      {
        id: 'buyerName',
        header: t('tenant.handelMagazyn.sales.columns.buyer'),
        accessor: (row) => row.buyerName ?? '—',
        defaultWidth: 260,
        renderCell: (_v: unknown, row: HmSaleListItem) => (
          <span className="flex items-center gap-1.5">
            <span className="truncate">{row.buyerName ?? '—'}</span>
            {/* Nabywca spoza kartoteki — bez tego znacznika wyglądałby jak zwykły kontrahent,
                a nie ma go gdzie „otworzyć". */}
            {row.buyerIsAdHoc && (
              <span className="shrink-0 rounded bg-bg-tertiary px-1.5 py-0.5 text-[11px] text-text-secondary">
                {t('tenant.handelMagazyn.adHoc.badge')}
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'typeCode',
        header: t('tenant.handelMagazyn.columns.typeCode'),
        accessor: (row) => row.typeCode,
        defaultWidth: 90,
      },
      {
        id: 'maturityDate',
        header: t('tenant.handelMagazyn.columns.maturityDate'),
        accessor: (row) => day(row.maturityDate),
        defaultWidth: 130,
      },
      {
        id: 'invoiceKind',
        header: t('tenant.handelMagazyn.ksef.kindColumn'),
        accessor: (row) => t(`tenant.handelMagazyn.ksef.kind.${row.invoiceKind}`),
        defaultWidth: 130,
        renderCell: (_v: unknown, row: HmSaleListItem) => (
          <InvoiceKindBadge kind={row.invoiceKind} />
        ),
      },
      {
        id: 'ksefStatus',
        header: t('tenant.handelMagazyn.ksef.statusColumn'),
        // Filtrowanie i sortowanie po KODZIE, nie po etykiecie — etykiet dla kodów nie mamy.
        accessor: (row) => row.ksefStatusCode ?? '',
        defaultWidth: 110,
        renderCell: (_v: unknown, row: HmSaleListItem) => (
          <KsefStatus code={row.ksefStatusCode} kind={row.invoiceKind} />
        ),
      },
      {
        id: 'ksefNumber',
        header: t('tenant.handelMagazyn.ksef.numberColumn'),
        accessor: (row) => row.ksefNumber ?? '—',
        defaultWidth: 280,
        renderCell: (_v: unknown, row: HmSaleListItem) =>
          row.ksefNumber ? (
            <span className="font-mono text-xs text-text-primary">{row.ksefNumber}</span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      },
      {
        id: 'ksefIssueDate',
        header: t('tenant.handelMagazyn.ksef.issueDateColumn'),
        accessor: (row) => day(row.ksefIssueDate),
        defaultWidth: 150,
      },
      // Stan obiegu akceptacji — jedyna kolumna czytana z NASZEJ bazy, nie z Symfonii.
      // Znika, gdy nie ma czego pokazać (brak firmy / modułu „Procesy" / połączenia).
      ...(approvalColumn ? [approvalColumn] : []),
    ],
    [t, approvalColumn],
  );

  // Raporty, których jeszcze nie ma, zostają w menu jako pozycje wyłączone — użytkownik Symfonii
  // spodziewa się tu konkretnego zestawu i ma widzieć, czego brakuje, a nie zgadywać.
  /**
   * Cztery zestawienia to jeden ekran z różnym wymiarem podsumowania — dlatego lista bierze się
   * z rejestru raportów, a nie z czterech wpisów przepisanych ręcznie.
   *
   * „Wykonaj dla zaznaczonych" to ten sam raport zawężony do wskazanych dokumentów; bez
   * zaznaczenia pozycja jest wyłączona, bo nie miałaby czego policzyć.
   */
  function reportItems(ctx: { dateFrom: string; dateTo: string; selected: HmSaleListItem[] }) {
    const ids = ctx.selected.map((row) => row.id);
    const open = (slug: SalesReportSlug, onlySelected: boolean) => () =>
      setReport({
        slug,
        dateFrom: ctx.dateFrom,
        dateTo: ctx.dateTo,
        ids: onlySelected ? ids : [],
      });

    const items = SALES_REPORT_SLUGS.map((slug) => ({
      key: slug,
      label: t(salesReportTitleKey(slug)),
      onSelect: open(slug, false),
    }));

    return [
      ...items,
      {
        key: 'selected',
        label: t('tenant.handelMagazyn.reports.forSelected', { count: ids.length }),
        onSelect: ids.length > 0 ? open('sprzedaz-za-okres', true) : undefined,
      },
    ];
  }

  return (
    <>
      <HmListShell<HmSaleListItem>
        title={t('tenant.handelMagazyn.sales.title')}
        resource="sales"
        queryKey="sales"
        tableId="hm_sales"
        testIdPrefix="hm-sales"
        columns={columns}
        getRowId={(row) => String(row.id)}
        emptyIcon={Receipt}
        groupBy={{ label: t('tenant.handelMagazyn.sales.kinds'), of: (row) => row.typeCode }}
        onRowClick={(row) => navigate(`/handel-magazyn/sprzedaz/${row.id}`)}
        onNew={() => navigate('/handel-magazyn/sprzedaz/nowy')}
        selectable
        renderSelectionAction={({ selected }) => (
          <HmBulkApprovalButton<HmSaleListItem>
            kind="sale"
            companyId={scope.companyId}
            selected={selected}
            getExternalId={(row) => row.id}
            getLabel={(row) => row.documentNumber}
          />
        )}
        renderReports={(ctx) => <ReportsMenuButton items={reportItems(ctx)} />}
        // Pola KSeF są tylko w szczegółach dokumentów, więc lista prosi o ich dociągnięcie jawnie.
        extraParams={{ withKsef: 'true' }}
        renderNotice={(data) => {
          if (!data.ksefSkipped) return null;
          return (
            <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {t('tenant.handelMagazyn.ksef.skipped')}
            </div>
          );
        }}
      />
      {report && (
        <SalesReportParamsModal
          open
          companyId={scope.companyId}
          title={t(salesReportTitleKey(report.slug))}
          // Okno startuje od ZAKRESU Z KARTOTEKI — użytkownik właśnie na nim patrzył, a przy
          // zaznaczonych dokumentach inny zakres po prostu by ich nie objął.
          initial={
            {
              ...reportDefaults(report.slug, defaultReportParams()),
              preset: 'custom',
              dateFrom: report.dateFrom,
              dateTo: report.dateTo,
              ids: report.ids,
            } satisfies SalesReportParams
          }
          onClose={() => setReport(null)}
          onSubmit={(params) => {
            navigate(`${salesReportPath(report.slug)}?${toSearchParams(params)}`);
            setReport(null);
          }}
        />
      )}
    </>
  );
}

export default HmSales;
